import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useUserPreferences } from './useUserPreferences';
import { useLocalStorage } from '@mantine/hooks';

// -----------------------------------------------------------------------------
export type LanguageCode = 'en' | 'el';

// -----------------------------------------------------------------------------
export type AvailableLanguage = {
  code: LanguageCode;
  name: string;
};

// -----------------------------------------------------------------------------
const availableLanguages = {
  el: {
    code: 'el',
    name: 'Ελληνικά',
  },
  en: {
    code: 'en',
    name: 'English',
  },
} as Record<LanguageCode, AvailableLanguage>;

// -----------------------------------------------------------------------------
const useLanguageSelector = (): {
  selectedLanguage: AvailableLanguage;
  changeLanguage: (languageCode: LanguageCode) => Promise<void>;
} => {
  const { i18n } = useTranslation();
  const { savePreferences } = useUserPreferences();

  // ---------------------------------------------------------------------------
  const [selectedLanguage, setSelectedLanguage] =
    useLocalStorage<AvailableLanguage>({
      key: 'selectedLanguage',
      defaultValue: availableLanguages.el,
    });

  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!selectedLanguage) {
      return;
    }

    if (i18n.language === selectedLanguage.code) {
      return;
    }

    i18n.changeLanguage(selectedLanguage.code).catch(console.error);
  }, [i18n, selectedLanguage]);

  // ---------------------------------------------------------------------------
  const changeLanguage = async (languageCode: LanguageCode): Promise<void> => {
    setSelectedLanguage(availableLanguages[languageCode]);
    await i18n.changeLanguage(languageCode);
    savePreferences();
  };

  // ---------------------------------------------------------------------------
  return {
    selectedLanguage,
    changeLanguage,
  };
};

export { availableLanguages, useLanguageSelector };
